
import { useState } from 'react';

const Photos = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const categories = [
    { id: 'all', label: 'All Photos', count: 24 },
    { id: 'progress', label: 'Progress Photos', count: 12 },
    { id: 'before', label: 'Before Photos', count: 6 },
    { id: 'after', label: 'After Photos', count: 4 },
    { id: 'team', label: 'Team Photos', count: 2 }
  ];

  const photos = [
    {
      id: 1,
      title: 'Initial Site Assessment',
      category: 'before',
      date: '2024-01-15',
      description: 'Initial state of the website before redesign',
      url: 'https://public.readdy.ai/ai/img_res/78901aab11a9d2515943afb7177af18c.jpg'
    },
    {
      id: 2,
      title: 'Wireframe Development',
      category: 'progress',
      date: '2024-01-18',
      description: 'Early wireframe sketches and user flow diagrams',
      url: 'https://readdy.ai/api/search-image?query=website%20wireframe%20sketches%20user%20interface%20design%20planning%20whiteboard%20drawings%20clean%20background&width=400&height=300&seq=2&orientation=landscape'
    },
    {
      id: 3,
      title: 'Design Mockups',
      category: 'progress',
      date: '2024-01-22',
      description: 'High-fidelity design mockups for homepage',
      url: 'https://readdy.ai/api/search-image?query=modern%20website%20design%20mockup%20clean%20interface%20blue%20color%20scheme%20professional%20layout%20desktop%20view&width=400&height=300&seq=3&orientation=landscape'
    },
    {
      id: 4,
      title: 'Mobile Responsive Design',
      category: 'progress',
      date: '2024-01-25',
      description: 'Mobile version design and responsive layouts',
      url: 'https://readdy.ai/api/search-image?query=mobile%20website%20design%20responsive%20layout%20smartphone%20screen%20modern%20interface%20clean%20background&width=400&height=300&seq=4&orientation=landscape'
    },
    {
      id: 5,
      title: 'Team Collaboration',
      category: 'team',
      date: '2024-01-28',
      description: 'Team working on project development',
      url: 'https://readdy.ai/api/search-image?query=professional%20team%20meeting%20office%20environment%20collaboration%20designers%20developers%20working%20together%20modern%20workspace&width=400&height=300&seq=5&orientation=landscape'
    },
    {
      id: 6,
      title: 'Final Homepage Design',
      category: 'after',
      date: '2024-02-01',
      description: 'Completed homepage with new branding',
      url: 'https://readdy.ai/api/search-image?query=finished%20website%20homepage%20modern%20design%20clean%20layout%20professional%20branding%20desktop%20computer%20screen%20final%20result&width=400&height=300&seq=6&orientation=landscape'
    }
  ];

  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">Project Photos</h3>
          <span className="text-sm text-gray-500">
            {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'}
          </span>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
            >
              <i className="ri-grid-line text-sm"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
            >
              <i className="ri-list-unordered text-sm"></i>
            </button>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
            <i className="ri-upload-line mr-2"></i>
            Upload Photos
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              selectedCategory === category.id
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.label} ({category.count})
          </button>
        ))}
      </div>

      {/* Photos Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPhotos.map((photo) => (
            <div key={photo.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-w-16 aspect-h-12">
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-48 object-cover object-top"
                />
              </div>
              <div className="p-4">
                <h4 className="font-medium text-gray-900 mb-1">{photo.title}</h4>
                <p className="text-sm text-gray-600 mb-2">{photo.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{photo.date}</span>
                  <span className="capitalize">{photo.category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPhotos.map((photo) => (
            <div key={photo.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center space-x-4 hover:shadow-md transition-shadow">
              <img
                src={photo.url}
                alt={photo.title}
                className="w-20 h-16 object-cover object-top rounded-md flex-shrink-0"
              />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">{photo.title}</h4>
                <p className="text-sm text-gray-600 mb-1">{photo.description}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>{photo.date}</span>
                  <span className="capitalize">{photo.category}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                  <i className="ri-download-line"></i>
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                  <i className="ri-share-line"></i>
                </button>
                <button className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100">
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredPhotos.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-image-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No photos found</h3>
          <p className="text-gray-500 mb-4">Upload some photos to get started</p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
            <i className="ri-upload-line mr-2"></i>
            Upload First Photo
          </button>
        </div>
      )}
    </div>
  );
};

export default Photos;
